import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@carvana/core-helios-button';
import { LoadingSpinner } from '@carvana/core-helios-loader';
import { datadogRum } from '@datadog/browser-rum';
import { Analytics } from '@services/analytics-service';
import { AnalyticEvents } from '@constants/index';
import { setConcernLocationDraftPromise } from '@store/sar-store';
import { useUserSelections } from '../../providers/user-selections';
import { Category, SubCategory, Location } from '../../constants';
import isCarvanaMobileAppUserAgent, { isAndroidCarvanaMobileAppUserAgent } from '../../../../utils/isCarvanaMobileApp';
import { useDamagePins, HOTSPOT_OVERFLOW_SELECTOR } from './useDamagePins';
import {
  getSpinnerAngleDeg,
  getFirstSpin,
  spinToAngle,
  resetSpinnerShapeMissReports,
  reportShapeMiss,
} from './spinnerMfeBridge';
import { useHideSpinnerChrome } from './useHideSpinnerChrome';
import {
  createRotationReporter,
  createIntroSpinInputReporter,
  createPinReporter,
  isIntroSpinPointer,
  INTRO_SPIN_POINTER_ID,
  monotonicNow,
} from './spinnerInteractionEvents';
import { runConfirmUpload } from './runConfirmUpload';
import SpinnerMfe from './SpinnerMfe';
import styles from './spinner-location.module.css';

type SpinnerLocationProps = {
  // The customer's own vehicle (resolved by the locations step: feed stockNumber,
  // ?stock= override for demos).
  stockNumber: number;
  // Same contract as SelectionList: advance the SAR flow once a location is chosen.
  handleNextRoute: (dataSubset: { title: Location }) => void;
  // The spinner can't render this vehicle (MFE load failure, or no usable 360° frames
  // on the CDN) — the locations step swaps back to the classic tile list.
  onUnavailable?: (reason: string) => void;
};

const CLICK_THRESHOLD_PX = 8;
const CLICK_THRESHOLD_MS = 400;
const DOUBLE_TAP_MS = 350;
const DOUBLE_TAP_PX = 30;

// Zoom via our own CSS-transform layer (keeps rotation working, unlike the spinner's
// native zoom which switches drag to pan). 1× to 4×.
const ZOOM_MIN = 1;
const ZOOM_MAX = 4;
const ZOOM_STEP = 0.3; // per wheel notch
// Start only slightly zoomed in: enough for the slider to read as a small zoom (thumb sits
// just off the left, ~3% of the track) while keeping the whole vehicle in frame on load in
// most cases. Higher values (e.g. 1.3) crop the ends of the car on initial render.
const ZOOM_INITIAL = 1.1;
const CONFIRM_ZOOM = 3; // auto-zoom level when verifying the marked spot
// Confirm-step zoom floor. In the confirm step the camera is pinned to the marked spot,
// so zooming all the way out lets the framing drift off the vehicle (the car can vanish).
// Keep a minimum so the marked area stays on-screen; the mark step still allows full 1x.
const CONFIRM_ZOOM_MIN = 2;
const CAMERA_MS = 700; // duration of the animated zoom-to-spot camera move
// Ease-out "camera swoosh": fast start, long soft landing (no abrupt stop).
const CAMERA_EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
// How long the confirm card takes to swipe back down on "Adjust location" (must match
// the .confirm-card transition in the CSS module).
const CARD_EXIT_MS = 360;
// If the car canvas isn't measurable by now, the vehicle has no usable 360° frames
// (or the MFE stalled) — hand back to the tile list instead of an empty pane.
const FRAMES_READY_TIMEOUT_MS = 25000;
// Zoom-slider resting offsets from the pane's bottom edge (applied as translateY so the
// slider's moves stay on the compositor alongside the card's).
const SLIDER_GAP_ABOVE_CARD_PX = 24; // hover gap over the confirm card
const SLIDER_FLOAT_PX = 64; // floating height in the mark step (above any bottom inset)
// The native app hosts this page in a webview that runs behind the app's bottom tab bar
// (49pt) + home-indicator safe area (34pt on notched iPhones), so anything anchored to
// the pane's bottom edge is covered by native chrome. Lift the bottom-anchored controls
// by this much: extra padding inside the confirm card (its measured offsetHeight then
// carries the inset into the slider hover spot and the camera targeting for free) and
// the same offset on the mark-step slider float.
const NATIVE_APP_BOTTOM_BAR_PX = 84;
const NATIVE_APP_BOTTOM_BAR_PX_ANDROID = 50;
// Fixed bottom cushion for mobile web (touch browsers). iOS Safari's bottom toolbar
// overlaps content but isn't reported by visualViewport, so it can't be measured —
// this guarantees the confirm card's CTA clears it. ~Safari toolbar height.
const MOBILE_WEB_BOTTOM_INSET_PX = 44;
// Comfort gap added on top of a MEASURED bottom inset (Android/Chrome reports its toolbar
// via visualViewport). Clearing the toolbar exactly leaves controls hugging it, so lift a
// little further for breathing room above the nav/toolbar.
const BOTTOM_COMFORT_GAP_PX = 24;
// visualViewport-reported obscured strip: ignore sub-pixel noise below this, and cap it so
// a transient (keyboard, page pinch) can't shove the bottom card off-screen.
const INSET_NOISE_FLOOR_PX = 4;
const MAX_BROWSER_BOTTOM_INSET_PX = 160;
// A tracked pointer not seen for this long is stale (its release was never delivered —
// possible on iOS when a pinch finger lifts outside the pane or the OS takes the
// gesture). Stale entries are pruned before any pinch decision.
const STALE_POINTER_MS = 3000;

export const SpinnerLocation = ({ stockNumber, handleNextRoute, onUnavailable }: SpinnerLocationProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  // User agent is stable for the life of the page — read it once.
  const [nativeAppInset] = useState(() => {
    if (!isCarvanaMobileAppUserAgent()) return 0;
    return isAndroidCarvanaMobileAppUserAgent() ? NATIVE_APP_BOTTOM_BAR_PX_ANDROID : NATIVE_APP_BOTTOM_BAR_PX;
  });
  // Mobile browser (not the native app): iOS Safari lays the page out to the TALL
  // viewport, so its bottom toolbar overlaps our bottom-anchored UI — and it does NOT
  // report that strip via visualViewport (clientHeight already excludes it), so we can't
  // measure it. Apply a fixed cushion on touch/coarse-pointer devices so the confirm
  // card's CTA always clears the toolbar. Desktop (fine pointer) gets 0.
  const [mobileWebInset] = useState(() => {
    if (isCarvanaMobileAppUserAgent()) return 0;
    return typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches
      ? MOBILE_WEB_BOTTOM_INSET_PX
      : 0;
  });
  // When the browser DOES report an obscured strip (some Androids, split-screen, etc.),
  // visualViewport catches it; we take the larger of the two.
  const [browserBottomInset, setBrowserBottomInset] = useState(0);
  useEffect(() => {
    if (nativeAppInset) return undefined; // native app: fixed tab-bar inset, below
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const update = () => {
      const obscured = Math.max(0, document.documentElement.clientHeight - vv.height - vv.offsetTop);
      setBrowserBottomInset(
        obscured > INSET_NOISE_FLOOR_PX ? Math.min(Math.round(obscured), MAX_BROWSER_BOTTOM_INSET_PX) : 0
      );
    };
    update();
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
    };
  }, [nativeAppInset]);
  // Android/Chrome reports its bottom toolbar via visualViewport (browserBottomInset > 0),
  // but lifting a control by EXACTLY that only cancels the overlap — it leaves the control
  // hugging the toolbar. Add a comfort gap on top of the measured inset. iOS Safari does
  // NOT report its toolbar (browserBottomInset ~0), so this branch stays off there and iOS
  // keeps riding on the fixed mobileWebInset, which already looks right.
  const browserComfortInset = browserBottomInset > 0 ? browserBottomInset + BOTTOM_COMFORT_GAP_PX : 0;
  // Slider (mark step) floats clear of the visible bottom: Android gets the toolbar + comfort
  // gap; on iOS the measured inset is ~0 so it stays modest (as before).
  const sliderInset = nativeAppInset || browserComfortInset;
  // The confirm card's CTA sits at the very bottom, so it also needs the mobile-web cushion
  // (the toolbar height iOS Safari's visualViewport can't see).
  const cardInset = nativeAppInset || Math.max(mobileWebInset, browserComfortInset);
  const { updateUserSelections } = useUserSelections();
  const { pins, canvasRect, addPin, clearPins, captureNow } = useDamagePins(containerRef);
  const [confirming, setConfirming] = useState(false);

  const downRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const isDraggingRef = useRef(false);
  const activePointersRef = useRef<Set<number>>(new Set());
  const lastUpRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const blockGestureRef = useRef(false);
  // Where the first finger's pointerdown landed (it passes through to the MFE) — a
  // pinch needs it to hand the MFE a synthetic cancel for that finger.
  const firstPointerDownRef = useRef<{ id: number; target: EventTarget | null } | null>(null);
  const syntheticCancelRef = useRef(false);
  // One-shot per session: capture the REAL computed touch-action on the first pane touch,
  // so we can confirm from device RUM whether Blink is honoring touch-action:none on the
  // actual touch target (the "Android rotate/pin dead" diagnosis).
  const touchDiagRef = useRef(false);

  // Zoom: a CSS scale on a wrapper around the spinner (so canvasRect + every pin track it
  // automatically). Driven by the slider, pinch, and wheel. Rotation still works at any
  // zoom — we never switch drag to pan.
  const [zoom, setZoom] = useState(ZOOM_INITIAL);
  const zoomRef = useRef(ZOOM_INITIAL);
  // t = last time this pointer was seen. Pointers whose release we never observe (an
  // iOS pinch finger lifting outside the pane, an OS-taken gesture) would otherwise sit
  // in this map forever — and a stale entry makes every later ONE-finger drag count as
  // two pointers, i.e. a phantom pinch: drags zoom instead of rotating (mobile-app bug).
  const pointersRef = useRef<Map<number, { x: number; y: number; t: number }>>(new Map());
  const pinchRef = useRef<{ startDist: number; startZoom: number } | null>(null);
  const applyZoom = useCallback((nextZoom: number) => {
    // Step-aware floor: the confirm step can't zoom out past CONFIRM_ZOOM_MIN (camera is
    // pinned to the marked spot there, so a full zoom-out loses the car). stepRef is the
    // source of truth the pointer handlers read; both the mark->confirm and
    // confirm->mark transitions set it before calling applyZoom, so this is correct.
    const min = stepRef.current === 'confirm' ? CONFIRM_ZOOM_MIN : ZOOM_MIN;
    const z = Math.max(min, Math.min(ZOOM_MAX, Math.round(nextZoom * 100) / 100));
    zoomRef.current = z;
    setZoom(z);
  }, []);

  const pin = pins[0];
  const hasPin = !!pin;
  const hasPinRef = useRef(false);
  hasPinRef.current = hasPin;
  const pinRef = useRef(pin);
  pinRef.current = pin;
  const canvasRectRef = useRef(canvasRect);
  canvasRectRef.current = canvasRect;

  // Imaging pipeline (clickstream-only for now): curated studio spins
  // (/spins/<id>/images/, white-cove backdrop) vs stabilized captures
  // (/executions/<id>/THE_3D_STABILIZER/, garage-bay backdrops). Rides the confirm
  // event so we can measure the mix + correlate LLM confidence by imaging type
  // before deciding whether stabilized spins need distinct display treatment.
  const [imagePipeline, setImagePipeline] = useState<'curated' | 'stabilized' | null>(null);
  const spinStyleRef = useRef<string | null>(null);
  useEffect(() => {
    if (!canvasRect || imagePipeline) return;
    const spin = getFirstSpin();
    const url = String(spin?.images?.urls?.[0] || '');
    if (!url) return;
    spinStyleRef.current = spin?.style ?? null;
    setImagePipeline(/THE_3D_STABILIZER|\/executions\//.test(url) ? 'stabilized' : 'curated');
  }, [canvasRect, imagePipeline]);

  // Keep the MFE's own chrome (immersive-exit "X", Exterior/Interior toggle, rotate hint,
  // 360° badge) hidden. One rAF-coalesced observer handles all of it — see the bridge's
  // hideSpinnerChrome for what and why. Tapping the exit "X", in particular, flips the MFE
  // to isReady=false and kills rotation + marking with no recovery, so it must stay hidden
  // even as the MFE asynchronously re-renders its controls.
  useHideSpinnerChrome(containerRef);

  // Unavailability: the MFE can load fine yet never render usable frames (e.g. a
  // vehicle sold long ago whose 360° data is gone from the CDN). If the car canvas
  // isn't measurable within the timeout — or the MFE bundle itself fails to load —
  // report up so the locations step swaps back to the classic tile list.
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;
  // Stable so SpinnerMfe (memoized) doesn't reconcile on every zoom/camera state change.
  const handleMfeFailed = useCallback(() => {
    datadogRum.addAction('sar-spinner-mfe-load-failed', { stockNumber });
    onUnavailableRef.current?.('mfe-load-failed');
  }, [stockNumber]);
  useEffect(() => {
    if (canvasRect) return undefined;

    let done = false;
    let pollTimer: number | undefined;
    let timeoutTimer: number | undefined;

    const frameCountOf = (s: ReturnType<typeof getFirstSpin>) =>
      (s?.images?.urls?.length || s?.cameras?.frameCount) ?? 0;

    const classify = () => {
      const spin = getFirstSpin();
      const urls = spin?.images?.urls ?? [];
      const key = (u: string) => {
        try {
          const parsed = new URL(u, window.location.href);
          return parsed.host + parsed.pathname;
        } catch {
          return u;
        }
      };
      let framesLoaded = 0;
      const getEntries = typeof performance !== 'undefined' && performance.getEntriesByType;
      if (urls.length && getEntries) {
        const loaded = new Set(performance.getEntriesByType('resource').map((e) => key(e.name)));
        framesLoaded = urls.reduce((n, u) => (loaded.has(key(u)) ? n + 1 : n), 0);
      }
      const connection = (navigator as unknown as {
        connection?: { effectiveType?: string; downlink?: number };
      }).connection;
      return {
        stockNumber,
        frameCount: frameCountOf(spin),
        framesLoaded,
        hasSpinNode: !!spin,
        effectiveType: connection?.effectiveType,
        downlink: connection?.downlink,
      };
    };

    const fallback = (reason: 'frames-timeout' | 'no-frames') => {
      if (done) return;
      done = true;
      window.clearTimeout(pollTimer);
      window.clearTimeout(timeoutTimer);
      try {
        datadogRum.addAction('sar-spinner-frames-timeout', { ...classify(), reason });
      } catch {
        /* swallow */
      }
      onUnavailableRef.current?.(reason);
    };

    const pollForNoData = () => {
      if (done) return;
      const spin = getFirstSpin();
      if (spin) {
        if (frameCountOf(spin) === 0) fallback('no-frames');
        return;
      }
      pollTimer = window.setTimeout(pollForNoData, 500);
    };

    timeoutTimer = window.setTimeout(() => fallback('frames-timeout'), FRAMES_READY_TIMEOUT_MS);
    pollForNoData();

    return () => {
      done = true;
      window.clearTimeout(pollTimer);
      window.clearTimeout(timeoutTimer);
    };
  }, [canvasRect, stockNumber]);

  // Two-step flow: 'mark' = spin + tap to place the dot; 'confirm' = ROTATION locked,
  // camera zoomed to the dot, bottom card asks the customer to confirm or adjust —
  // tapping still re-marks (the camera re-centers on each new dot); only rotating
  // requires going back via "Adjust location". stepRef mirrors the state for the
  // pointer handlers (bound once) so they can gate rotation without re-binding.
  const [step, setStep] = useState<'mark' | 'confirm'>('mark');
  const stepRef = useRef<'mark' | 'confirm'>('mark');
  // The MFE "swoops" in as it loads/settles — ugly. Keep the car hidden behind a loading
  // spinner until we're about to drive the intro spin (MFE ready + swoop done), then reveal.
  const [revealed, setRevealed] = useState(false);

  // Camera model: the zoom layer's transform is a single translate3d + scale with a
  // fixed 0,0 origin (one compositor-accelerated property — animating transform-origin
  // would run on the main thread and stutter against the heavy spinner canvas).
  // 'center' keeps the pane midpoint fixed while zooming (mark step); 'point' pins the
  // marked spot p (layer-local px) to the on-screen target t (confirm step), which also
  // keeps the dot centered while the customer works the slider. The transition is only
  // enabled for the camera move itself, so slider/pinch stay snappy.
  const [camera, setCamera] = useState<
    { mode: 'center' } | { mode: 'point'; p: { x: number; y: number }; t: { x: number; y: number } }
  >({ mode: 'center' });
  const [zoomAnimated, setZoomAnimated] = useState(false);
  const zoomAnimTimerRef = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(zoomAnimTimerRef.current), []);

  // "Adjust location" exit: the card stays mounted for CARD_EXIT_MS while it translates
  // down below the pane edge, so it swipes away instead of vanishing.
  const [cardExiting, setCardExiting] = useState(false);
  const cardExitTimerRef = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(cardExitTimerRef.current), []);

  // Measured card height — positions the slider's hover spot above the card (the card
  // itself is bottom-anchored, so only the slider needs the number).
  const [cardHeight, setCardHeight] = useState(0);

  // Pane size (for the translate math at render time), kept fresh by a ResizeObserver.
  const [paneSize, setPaneSize] = useState({ w: 1, h: 1 });
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const measure = () => setPaneSize({ w: el.clientWidth || 1, h: el.clientHeight || 1 });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const viewedAtRef = useRef<number | null>(null);
  const interactiveAtRef = useRef<number | null>(null);
  const revealedRef = useRef(false);
  const rotationReporterRef = useRef<ReturnType<typeof createRotationReporter> | null>(null);
  if (!rotationReporterRef.current) rotationReporterRef.current = createRotationReporter();
  const introSpinLiveRef = useRef(false);
  const introSpinTakeoverRef = useRef<(() => void) | null>(null);
  const introSpinInputReporterRef = useRef<ReturnType<typeof createIntroSpinInputReporter> | null>(null);
  if (!introSpinInputReporterRef.current) introSpinInputReporterRef.current = createIntroSpinInputReporter();
  const pinReporterRef = useRef<ReturnType<typeof createPinReporter> | null>(null);
  if (!pinReporterRef.current) pinReporterRef.current = createPinReporter();

  useEffect(() => {
    revealedRef.current = revealed;
    if (revealed && interactiveAtRef.current === null) interactiveAtRef.current = monotonicNow();
  }, [revealed]);

  useEffect(() => {
    // Fresh spinner session: allow the bridge to re-report any MFE-shape misses once each.
    resetSpinnerShapeMissReports();
    if (viewedAtRef.current === null) viewedAtRef.current = monotonicNow();
    Analytics.trackEvent(AnalyticEvents.sarLocationViewed, { variant: 'spinner' });
    datadogRum.addAction(AnalyticEvents.sarLocationViewed, {
      variant: 'spinner',
      nativeApp: !!nativeAppInset,
    });
  }, [nativeAppInset]);

  // On load, spin one full revolution and land on the driver-side profile (90° = front
  // pointing left), then hand control to the customer. This SIMULATES A SWIPE — the MFE's
  // native rotation path — by dispatching a continuous pointer drag on its canvas and moving
  // the pointer a few px per frame. That rotates the car in real time with no per-call tween,
  // so it's fluid. Closed-loop: we read the live angle and keep dragging until a full turn is
  // done, then ease onto the driver side and release. If the synthetic drag produces NO
  // rotation (some players ignore untrusted events), we fall back to the stepped spinToAngle
  // walk so it can never end up worse. Bails the moment the customer starts marking.
  useEffect(() => {
    // Resting frame (the spin pre-positions here, does one turn, and settles back here). This
    // is THE knob for where it stops: lower = less rotation / more toward the front; raise it
    // if it stops too far toward the front. ~72 lands on the clean driver-side profile.
    const TARGET = 72;
    const FULL_TURN = 360; // exactly one revolution (we pre-position on TARGET, so it ends there too)
    const DRAG_DIR = -1; // drag direction → spin sense. -1 = clockwise; flip to +1 for counter-clockwise.
    const START_DELAY_MS = 400; // let the first frames paint before spinning
    const STEP_MS = 16; // ~60fps drag cadence
    const DX_TURN = 6; // px moved per frame during the main turn (bigger = faster)
    const DX_LAND = 2; // px per frame while easing into the finish
    const LAND_ZONE = 30; // ease to the slow drag speed over the final degrees
    const NONRESP_STEPS = 30; // if barely rotated after this many frames → treat as no-response
    const NONRESP_DEG = 5; // ...where "barely" means less than this much travel
    const MAX_STEPS = 1000; // hard safety cap
    const MAX_SPIN_MS = 8000; // wall-clock cap; MAX_STEPS alone assumes the loop ticks at STEP_MS

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    let startTimer: ReturnType<typeof setTimeout>;
    let settleTimer: ReturnType<typeof setTimeout>;

    const norm = (d: number) => ((d % 360) + 360) % 360;
    const gap = (a: number, b: number) => {
      const d = Math.abs(norm(a) - norm(b));
      return Math.min(d, 360 - d);
    };
    const signed = (from: number, to: number) => ((to - from + 540) % 360) - 180; // [-180,180]

    // Lock exactly on the driver side once the motion is done.
    let settleAttempts = 0;
    const settle = () => {
      if (cancelled || hasPinRef.current) return;
      const angle = getSpinnerAngleDeg();
      if (angle == null || gap(angle, TARGET) > 3) spinToAngle(TARGET);
      settleAttempts += 1;
      if (settleAttempts < 15 && (angle == null || gap(angle, TARGET) > 3)) {
        settleTimer = setTimeout(settle, 150);
      }
    };

    // The MFE's car <canvas> — the element its drag handler listens on. Pick the largest.
    const findCanvas = (): HTMLCanvasElement | null => {
      const root = document.querySelector('[data-qa="spinner-app-container"]') || containerRef.current;
      if (!root) return null;
      let best: HTMLCanvasElement | null = null;
      let bestArea = 0;
      root.querySelectorAll('canvas').forEach((c) => {
        const r = c.getBoundingClientRect();
        const area = r.width * r.height;
        if (area > bestArea) {
          bestArea = area;
          best = c as HTMLCanvasElement;
        }
      });
      return best;
    };

    const send = (el: Element, type: string, x: number, y: number, id: number, down: boolean) => {
      try {
        el.dispatchEvent(
          new PointerEvent(type, {
            pointerId: id,
            pointerType: 'mouse',
            isPrimary: true,
            bubbles: true,
            cancelable: true,
            composed: true,
            clientX: x,
            clientY: y,
            button: 0,
            buttons: down ? 1 : 0,
            view: window,
          })
        );
      } catch {
        /* PointerEvent ctor unsupported → swipe just won't fire; fallback covers it */
      }
    };

    // Stepped spinToAngle fallback (only used if the swipe produces no rotation).
    const runStepped = (a0: number) => {
      const HOP = 12;
      const hops = Math.max(6, Math.round(FULL_TURN / HOP));
      const stepDeg = (DRAG_DIR * FULL_TURN) / hops; // signed so the fallback matches the swipe's direction
      const wps: number[] = [];
      for (let k = 1; k <= hops; k++) wps.push(norm(a0 + stepDeg * k));
      let i = 0;
      let waited = 0;
      spinToAngle(wps[0]);
      const t = () => {
        if (cancelled || hasPinRef.current) return;
        const ang = getSpinnerAngleDeg();
        waited += 25;
        if ((ang != null && gap(ang, wps[i]) <= 6) || waited >= 150) {
          i += 1;
          waited = 0;
          if (i >= wps.length) {
            settle();
            return;
          }
          spinToAngle(wps[i]);
        }
        timer = setTimeout(t, 25);
      };
      timer = setTimeout(t, 25);
    };

    const runSwipe = (canvas: HTMLCanvasElement, a0: number) => {
      const r = canvas.getBoundingClientRect();
      const y = r.top + r.height / 2;
      let x = r.left + r.width / 2;
      const id = INTRO_SPIN_POINTER_ID;
      let last = a0;
      let traveled = 0;
      let steps = 0;
      let released = false;
      const startedAt = monotonicNow();

      // Total forward rotation for a single revolution that lands on the driver side.
      const release = () => {
        introSpinTakeoverRef.current = null;
        if (released) return;
        released = true;
        introSpinLiveRef.current = false;
        send(canvas, 'pointerup', x, y, id, false);
      };

      introSpinTakeoverRef.current = () => {
        cancelled = true;
        clearTimeout(timer);
        clearTimeout(settleTimer);
        release();
      };

      introSpinLiveRef.current = true;
      send(canvas, 'pointerdown', x, y, id, true); // grab
      const stepFn = () => {
        if (cancelled || hasPinRef.current) {
          release();
          return;
        }
        const slowing = traveled >= FULL_TURN - LAND_ZONE; // ease in over the final degrees
        x += DRAG_DIR * (slowing ? DX_LAND : DX_TURN); // DRAG_DIR sets the spin direction (clockwise)
        send(canvas, 'pointermove', x, y, id, true);
        const cur = getSpinnerAngleDeg();
        if (cur != null) {
          traveled += Math.abs(signed(last, cur));
          last = cur;
        }
        steps += 1;

        // No-response guard: synthetic events aren't rotating it → fall back to stepped.
        if (steps >= NONRESP_STEPS && traveled < NONRESP_DEG) {
          release();
          runStepped(a0);
          return;
        }
        // Land on the ACTUAL angle as the car comes back around near the end of the turn — the
        // store angle lags the render, so stopping on the travel counter alone overshoots. Once
        // we're most of the way around AND the live angle is back on the driver side, release.
        // Hard caps (a bit past a full turn / max steps) are safety only.
        const nearEnd = traveled >= FULL_TURN * 0.9;
        const backOnTarget = cur != null && gap(cur, TARGET) <= 3;
        const outOfTime = monotonicNow() - startedAt >= MAX_SPIN_MS;
        if ((nearEnd && backOnTarget) || traveled >= FULL_TURN + 45 || steps >= MAX_STEPS || outOfTime) {
          release();
          settle(); // lock exactly on the driver side
          return;
        }
        timer = setTimeout(stepFn, STEP_MS);
      };
      timer = setTimeout(stepFn, STEP_MS);
    };

    let positioned = false;
    let stableAngle: number | null = null;
    let stableCount = 0;
    let beginAttempts = 0;
    const begin = () => {
      if (cancelled || hasPinRef.current) return;
      beginAttempts += 1;
      const a0 = getSpinnerAngleDeg();
      const canvas = findCanvas();
      // Store/frames/canvas not ready yet — retry shortly.
      if (a0 == null || !canvas) {
        if (beginAttempts < 60) {
          startTimer = setTimeout(begin, 150);
          return;
        }
        // Attempts exhausted: the MFE's angle store (or canvas) never became readable — a
        // store-shape drift or degraded vehicle data. The intro spin is a nicety; staying
        // hidden is not: the opaque loading overlay blocks every tap, and the frames-ready
        // timeout won't rescue us because the canvas rect IS measurable. Reveal without the
        // intro spin so the customer can rotate and mark. Truly frame-less vehicles are
        // still handled by the frames-ready timeout (tile-list fallback).
        reportShapeMiss('intro-spin-skipped');
        setRevealed(true);
        return;
      }
      // Phase 1 (hidden behind the loader): pre-position on the driver side so the one-turn
      // spin both starts AND ends there. This also absorbs the MFE's "swoop".
      if (!positioned && beginAttempts < 45) {
        if (gap(a0, TARGET) > 2) {
          spinToAngle(TARGET);
          startTimer = setTimeout(begin, 120);
          return;
        }
        positioned = true;
      }
      // Phase 2 (hidden): wait for the angle to hold steady across a couple of reads (the
      // pre-position tween has settled) before revealing. Capped so a jittery read can't keep
      // the loader up forever.
      if (beginAttempts < 50 && (stableAngle == null || Math.abs(a0 - stableAngle) > 1)) {
        stableAngle = a0;
        stableCount = 0;
        startTimer = setTimeout(begin, 120);
        return;
      }
      if (beginAttempts < 50 && stableCount < 2) {
        stableCount += 1;
        startTimer = setTimeout(begin, 120);
        return;
      }
      setRevealed(true); // MFE ready, positioned + settled → reveal and play the one-turn spin
      runSwipe(canvas, a0);
    };
    startTimer = setTimeout(begin, START_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      clearTimeout(startTimer);
      clearTimeout(settleTimer);
      introSpinTakeoverRef.current?.();
      introSpinTakeoverRef.current = null;
      introSpinLiveRef.current = false;
    };
  }, []);

  // Placing a dot advances to the confirm step (spin locks; the camera move runs in the
  // effect below once the card has rendered, so it can measure the visible image area).
  useEffect(() => {
    if (!hasPin || stepRef.current !== 'mark') return;
    stepRef.current = 'confirm';
    setStep('confirm');
    datadogRum.addAction('sar-spinner-confirm-step-viewed', { variant: 'spinner' });
  }, [hasPin]);

  // Camera move: zoom in with the dot landing CENTERED in the image area that stays
  // visible above the confirm card. Runs after the card mounts (so its height is
  // measurable) AND again whenever a re-tap places a new pin during the confirm step
  // (pin.id in the deps), gliding the camera to the new spot at the current zoom.
  // canvasRect is already in the zoom layer's local space, so the pin's local
  // position p reads straight off it; the render below translates p to target t.
  const confirmCardRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (step !== 'confirm') return;
    const z = Math.max(zoomRef.current, CONFIRM_ZOOM);
    const el = containerRef.current;
    const cr = canvasRectRef.current;
    const p = pinRef.current;
    // Measure the card independently of the canvas guard below — the slider's hover
    // position must be right even if the camera targeting is skipped.
    const cardH = confirmCardRef.current?.offsetHeight ?? 0;
    if (cardH) setCardHeight(cardH);
    if (el && cr && p) {
      const w = el.clientWidth || 1;
      const h = el.clientHeight || 1;
      const px = cr.left + p.canvasFraction.x * cr.width;
      const py = cr.top + p.canvasFraction.y * cr.height;
      setCamera({ mode: 'point', p: { x: px, y: py }, t: { x: w / 2, y: Math.max(0, h - cardH) / 2 } });
    }
    setZoomAnimated(true);
    applyZoom(z);
    window.clearTimeout(zoomAnimTimerRef.current);
    zoomAnimTimerRef.current = window.setTimeout(() => setZoomAnimated(false), CAMERA_MS);
  }, [step, pin?.id, applyZoom]);

  // "Adjust location": back to the mark step — dot cleared, spin unlocked, camera
  // eased back out to the initial framing.
  const adjustLocation = useCallback(() => {
    Analytics.trackEvent(AnalyticEvents.sarLocationSpinnerAdjustLocation, { variant: 'spinner' });
    datadogRum.addAction('sar-spinner-adjust-location', { variant: 'spinner' });
    clearPins();
    // Drop any pending draft from an earlier confirmed spot so a later ASR can't persist an
    // abandoned selection.
    setConcernLocationDraftPromise(null);
    stepRef.current = 'mark';
    setStep('mark');
    // Card swipes down while the camera zooms back out to the initial framing — the
    // two run concurrently, and rotation unlocks for a fresh pass at the vehicle.
    setCardExiting(true);
    window.clearTimeout(cardExitTimerRef.current);
    cardExitTimerRef.current = window.setTimeout(() => setCardExiting(false), CARD_EXIT_MS);
    setZoomAnimated(true);
    setCamera({ mode: 'center' });
    applyZoom(ZOOM_INITIAL);
    window.clearTimeout(zoomAnimTimerRef.current);
    zoomAnimTimerRef.current = window.setTimeout(() => setZoomAnimated(false), CAMERA_MS);
  }, [clearPins, applyZoom]);

  // Click-to-mark vs drag-to-rotate, with pinch/double-tap zoom suppressed. Ported
  // from the POC's SpinnerDamagePicker pointer handling (3D/panel-map removed).
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const swallow = (e: Event) => {
      e.stopImmediatePropagation();
      e.preventDefault();
    };

    // Every handler that touches the gesture bookkeeping below must be built through this.
    const ignoreIntroSpin =
      (handler: (e: PointerEvent) => void) =>
      (e: PointerEvent): void => {
        if (isIntroSpinPointer(e)) return;
        handler(e);
      };

    // A clean tap (re)marks the spot — used by both steps; tapping again just moves
    // the pin to the new location.
    const markAtPointer = (e: PointerEvent) => {
      // Not until the car canvas is actually rendered (same gate as the slider's
      // fade-in): a too-early tap would otherwise advance to the confirm step with a
      // pin that has no measurable geometry — no visible dot, camera math skipped,
      // and the slider stranded at its floating offset on top of the card.
      if (!canvasRectRef.current) return;
      const rect = el.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (x < 0 || x > rect.width || y < 0 || y > rect.height) return;
      const overflowEl = el.querySelector<HTMLElement>(HOTSPOT_OVERFLOW_SELECTOR);
      let pinX = x;
      let pinY = y;
      if (overflowEl) {
        const overflowRect = overflowEl.getBoundingClientRect();
        pinX = e.clientX - overflowRect.left;
        pinY = e.clientY - overflowRect.top;
      }
      // Replace any existing pin so only the latest tap is marked.
      clearPins();
      const placedId = addPin(pinX, pinY, stockNumber);
      // Direct marking-health signal. A clean tap reached here with the canvas ready, so a
      // pin MUST result; anything else is the silent "taps do nothing" failure that hid
      // SERV-583. Emit success (sliced by whether we're on the degraded 2D-only path) and,
      // critically, a distinct failure alarm so any future regression is visible in RUM
      // instead of silent. This is the signal the original bug lacked.
      if (placedId) {
        pinReporterRef.current?.reportPinPlaced({ stockNumber, degraded: !overflowEl });
      } else {
        pinReporterRef.current?.reportPinFailed({ stockNumber });
      }
    };

    const onDown = ignoreIntroSpin((e: PointerEvent) => {
      // Interactions on the zoom slider / confirm card must not mark or rotate — leave
      // them to their own controls.
      if ((e.target as HTMLElement)?.closest?.('[data-zoom-ui]')) return;

      if (introSpinLiveRef.current) {
        introSpinInputReporterRef.current?.({
          pointerType: e.pointerType,
          stockNumber,
          dropped: pointersRef.current.has(INTRO_SPIN_POINTER_ID),
        });
        introSpinTakeoverRef.current?.();
      }

      if (!touchDiagRef.current) {
        touchDiagRef.current = true;
        try {
          const tgt = e.target as HTMLElement;
          datadogRum.addAction('sar-spinner-touch-diagnostic', {
            pointerType: e.pointerType,
            canvasReady: !!canvasRectRef.current,
            paneTouchAction: getComputedStyle(el).touchAction,
            targetTag: tgt?.tagName,
            targetTouchAction: tgt ? getComputedStyle(tgt).touchAction : null,
          });
        } catch {
          /* diagnostics must never affect the gesture */
        }
      }

      const now0 = Date.now();
      // Prune pointers whose release we never saw — otherwise this new (single) finger
      // pairs with a ghost and the drag becomes a phantom pinch.
      pointersRef.current.forEach((p, id) => {
        if (id !== e.pointerId && now0 - p.t > STALE_POINTER_MS) {
          pointersRef.current.delete(id);
          activePointersRef.current.delete(id);
        }
      });
      activePointersRef.current.add(e.pointerId);
      pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY, t: now0 });
      // Two fingers → pinch-zoom our own layer (suppress the spinner's native pinch).
      if (pointersRef.current.size >= 2) {
        // The MFE already received the first finger's pointerdown (that's how
        // drag-rotate works) and will see nothing further — we swallow the whole
        // pinch. Without closure it wedges mid-drag and rotation stays dead after the
        // pinch (confirmed from device RUM: taps keep marking, spins stop). Hand it a
        // synthetic pointerUP for that finger so its drag ends like a normal tap —
        // NOT a pointercancel, which the MFE reacts to by breaking its canvas state
        // (verified locally: post-pinch taps stop marking after a cancel reaches it).
        // The guard flag keeps our own listeners from reacting to the synthetic event.
        const first = firstPointerDownRef.current;
        if (first && first.id !== e.pointerId && first.target) {
          syntheticCancelRef.current = true;
          try {
            const prev = pointersRef.current.get(first.id);
            first.target.dispatchEvent(new PointerEvent('pointerup', {
              bubbles: true,
              pointerId: first.id,
              pointerType: e.pointerType,
              clientX: prev?.x,
              clientY: prev?.y,
            }));
          } catch {
            // PointerEvent construction can't realistically fail on supported browsers.
          }
          syntheticCancelRef.current = false;
          firstPointerDownRef.current = null;
        }
        // Collect via forEach — the webpack build targets ES5, which can't spread iterators.
        const pts: { x: number; y: number }[] = [];
        pointersRef.current.forEach((p) => pts.push(p));
        const [a, b] = pts;
        pinchRef.current = { startDist: Math.hypot(a.x - b.x, a.y - b.y) || 1, startZoom: zoomRef.current };
        blockGestureRef.current = true;
        downRef.current = null;
        swallow(e);
        return;
      }
      // Confirm step: ROTATION is locked but marking isn't. Swallow the event so the
      // spinner MFE can never start a rotate drag, while our own tap tracking stays
      // live so a clean tap re-marks (pinch above + wheel/slider still zoom).
      if (stepRef.current === 'confirm') {
        blockGestureRef.current = false;
        downRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
        isDraggingRef.current = false;
        swallow(e);
        return;
      }
      const last = lastUpRef.current;
      const now = Date.now();
      // A quick second tap near the same spot would trigger the spinner's native double-tap
      // zoom — swallow it so that can't fire, but still fall through and treat it as a normal
      // tap so re-marking the same area keeps working (we bind no action to double-tap).
      if (last && now - last.time < DOUBLE_TAP_MS && Math.hypot(e.clientX - last.x, e.clientY - last.y) < DOUBLE_TAP_PX) {
        swallow(e);
      }
      blockGestureRef.current = false;
      downRef.current = { x: e.clientX, y: e.clientY, time: now };
      isDraggingRef.current = false;
      // This down reaches the MFE (drag-rotate); remember it so a pinch can cancel it.
      firstPointerDownRef.current = { id: e.pointerId, target: e.target };
    });

    const onMove = ignoreIntroSpin((e: PointerEvent) => {
      // Pinch in progress → drive zoom from the live finger span.
      if (pinchRef.current && pointersRef.current.has(e.pointerId)) {
        pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY, t: Date.now() });
        const pts: { x: number; y: number }[] = [];
        pointersRef.current.forEach((p) => pts.push(p));
        if (pts.length >= 2) {
          const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
          applyZoom(pinchRef.current.startZoom * (dist / pinchRef.current.startDist));
        }
        swallow(e);
        return;
      }
      if (blockGestureRef.current || activePointersRef.current.size >= 2) {
        swallow(e);
        return;
      }
      // Confirm step: single-pointer moves never reach the spinner (no rotation), but
      // keep measuring the drag so a scrub doesn't count as a tap on release. The
      // existing dot stays put — only a successful new tap replaces it.
      if (stepRef.current === 'confirm') {
        if (downRef.current) {
          const dx = Math.abs(e.clientX - downRef.current.x);
          const dy = Math.abs(e.clientY - downRef.current.y);
          if (dx > CLICK_THRESHOLD_PX || dy > CLICK_THRESHOLD_PX) isDraggingRef.current = true;
        }
        swallow(e);
        return;
      }
      if (!downRef.current) return;
      const totalDx = Math.abs(e.clientX - downRef.current.x);
      const totalDy = Math.abs(e.clientY - downRef.current.y);
      if (totalDx > CLICK_THRESHOLD_PX || totalDy > CLICK_THRESHOLD_PX) {
        if (!isDraggingRef.current) {
          // First movement of a drag = the customer is rotating the vehicle. Clear the
          // marked spot: the pin is anchored to the old frame and no longer matches the
          // new view, so the selection is no longer valid.
          isDraggingRef.current = true;
          clearPins();
          // Closed-hand cursor while actively rotating (inline so it wins over the
          // class-level grab; cleared on release/cancel).
          el.style.cursor = 'grabbing';
          rotationReporterRef.current?.({
            userInitiated: e.isTrusted,
            ready: revealedRef.current,
            step: stepRef.current,
            pinching: pinchRef.current !== null,
            blocked: blockGestureRef.current,
            pointerCount: activePointersRef.current.size,
            pastDragThreshold: totalDx > CLICK_THRESHOLD_PX || totalDy > CLICK_THRESHOLD_PX,
            pointerType: e.pointerType,
            stockNumber,
            viewedAt: viewedAtRef.current,
            interactiveAt: interactiveAtRef.current,
          });
        }
      }
    });

    const onUp = ignoreIntroSpin((e: PointerEvent) => {
      if (syntheticCancelRef.current) return;
      activePointersRef.current.delete(e.pointerId);
      pointersRef.current.delete(e.pointerId);
      if (firstPointerDownRef.current?.id === e.pointerId) firstPointerDownRef.current = null;
      if (pointersRef.current.size < 2) pinchRef.current = null;
      lastUpRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
      el.style.cursor = ''; // back to the class-level cursor (grab / pointer)

      if (blockGestureRef.current) {
        if (activePointersRef.current.size === 0) blockGestureRef.current = false;
        swallow(e);
        return;
      }
      // Confirm step: a clean tap re-marks the spot (the camera then re-centers on it);
      // anything drag-like does nothing — rotation stays locked until Adjust location.
      if (stepRef.current === 'confirm') {
        swallow(e);
        if (downRef.current && !isDraggingRef.current && Date.now() - downRef.current.time < CLICK_THRESHOLD_MS) {
          markAtPointer(e);
        }
        downRef.current = null;
        isDraggingRef.current = false;
        return;
      }
      if (!downRef.current) return;
      const dt = Date.now() - downRef.current.time;

      if (!isDraggingRef.current && dt < CLICK_THRESHOLD_MS) {
        markAtPointer(e);
      }
      // No snap-to-side on release — the vehicle rests wherever the customer leaves it
      // (free rotation). Detection still works at any angle (the prompt carries the
      // turntable angle + nose hint).

      downRef.current = null;
      isDraggingRef.current = false;
    });

    const onCancel = ignoreIntroSpin((e: PointerEvent) => {
      if (syntheticCancelRef.current) return;
      activePointersRef.current.delete(e.pointerId);
      pointersRef.current.delete(e.pointerId);
      if (firstPointerDownRef.current?.id === e.pointerId) firstPointerDownRef.current = null;
      if (pointersRef.current.size < 2) pinchRef.current = null;
      if (activePointersRef.current.size === 0) blockGestureRef.current = false;
      el.style.cursor = '';
      // Keep pointercancel away from the MFE. Mobile browsers fire real cancels when
      // the OS involves itself in a gesture (pinch being the prime case), and the MFE's
      // reaction to mid-gesture cancels is a suspect in the "pinch breaks rotation"
      // mobile bug. Our own tracking above already handled the cancellation. The
      // telemetry tells us from real devices whether cancels fire at all here.
      datadogRum.addAction('sar-spinner-pointercancel', {
        pointerType: e.pointerType,
        remainingPointers: pointersRef.current.size,
      });
      swallow(e);
    });

    const onTouchMulti = (e: TouchEvent) => {
      if (e.touches.length > 1) swallow(e);
    };
    const onGesture = (e: Event) => swallow(e);
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      applyZoom(zoomRef.current + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
    };
    const onDblClick = (e: MouseEvent) => {
      e.preventDefault();
      e.stopImmediatePropagation();
    };
    // SERV-583 (bug 3): a clean tap is OUR mark gesture — the MFE must never treat it as a
    // click. Its hotspot chips / tour cards / expanded-image view are click-driven (React
    // onClick), and on vehicles without a projected-hotspot layer that UI isn't inside the
    // containers our CSS hides, so a tap opened a zoomed hotspot photo with no way back.
    // Swallow every click that isn't on our own overlay controls; rotation and marking are
    // pointer-event driven and unaffected.
    const onClick = (e: MouseEvent) => {
      if ((e.target as HTMLElement)?.closest?.('[data-zoom-ui]')) return;
      swallow(e);
    };

    // Releases that land OUTSIDE the pane never reach the el-bound listeners above —
    // the classic iOS case is a pinch finger sliding past the pane edge before lifting.
    // Bubble-phase window listeners catch those and clear the tracking state (events
    // the el handlers swallowed don't bubble here, so in-pane handling is unaffected).
    const onWindowRelease = ignoreIntroSpin((e: PointerEvent) => {
      if (syntheticCancelRef.current) return;
      if (!activePointersRef.current.has(e.pointerId) && !pointersRef.current.has(e.pointerId)) return;
      activePointersRef.current.delete(e.pointerId);
      pointersRef.current.delete(e.pointerId);
      if (pointersRef.current.size < 2) pinchRef.current = null;
      if (activePointersRef.current.size === 0) blockGestureRef.current = false;
    });
    // App backgrounded mid-gesture (webview suspends, no release events ever arrive):
    // drop all pointer state so the pane comes back interactive.
    const resetPointerState = () => {
      activePointersRef.current.clear();
      pointersRef.current.clear();
      pinchRef.current = null;
      blockGestureRef.current = false;
      downRef.current = null;
      isDraggingRef.current = false;
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') resetPointerState();
    };

    el.addEventListener('pointerdown', onDown, true);
    el.addEventListener('pointermove', onMove, true);
    el.addEventListener('pointerup', onUp, true);
    el.addEventListener('pointercancel', onCancel, true);
    window.addEventListener('pointerup', onWindowRelease);
    window.addEventListener('pointercancel', onWindowRelease);
    window.addEventListener('blur', resetPointerState);
    document.addEventListener('visibilitychange', onVisibility);
    el.addEventListener('touchstart', onTouchMulti, { capture: true, passive: false });
    el.addEventListener('touchmove', onTouchMulti, { capture: true, passive: false });
    el.addEventListener('gesturestart', onGesture, true);
    el.addEventListener('gesturechange', onGesture, true);
    el.addEventListener('gestureend', onGesture, true);
    el.addEventListener('wheel', onWheel, { capture: true, passive: false });
    el.addEventListener('dblclick', onDblClick, true);
    el.addEventListener('click', onClick, true);
    return () => {
      el.removeEventListener('pointerdown', onDown, true);
      el.removeEventListener('pointermove', onMove, true);
      el.removeEventListener('pointerup', onUp, true);
      el.removeEventListener('pointercancel', onCancel, true);
      el.removeEventListener('touchstart', onTouchMulti, true);
      el.removeEventListener('touchmove', onTouchMulti, true);
      el.removeEventListener('gesturestart', onGesture, true);
      el.removeEventListener('gesturechange', onGesture, true);
      el.removeEventListener('gestureend', onGesture, true);
      el.removeEventListener('wheel', onWheel, true);
      el.removeEventListener('dblclick', onDblClick, true);
      el.removeEventListener('click', onClick, true);
      window.removeEventListener('pointerup', onWindowRelease);
      window.removeEventListener('pointercancel', onWindowRelease);
      window.removeEventListener('blur', resetPointerState);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [stockNumber, addPin, clearPins, applyZoom]);

  const confirm = useCallback(() => {
    if (!pin || confirming) return;
    Analytics.trackEvent(AnalyticEvents.sarLocationSpinnerConfirmClicked, { variant: 'spinner', stockNumber });
    setConfirming(true);
    const capturedPin = pin;

    // The spinner only renders for Cosmetic → Exterior; force those selections here
    // (updateUserSelections resets deeper levels when a higher one changes, so the calls
    // are ordered category → subcategory → location). The LOCATION starts as 'Other', a
    // real Exterior RepairIssueLocation row, and is swapped for the LLM-detected panel
    // when the background upload returns — always well before the coverage step's
    // saveuserselections reads it, unless the detection fails or the customer outruns it,
    // in which case 'Other' is the honest answer (the ConcernLocation row still carries
    // whatever the model said).
    updateUserSelections({ category: Category.Cosmetic });
    updateUserSelections({ subcategory: SubCategory.Exterior });
    updateUserSelections({ location: Location.Other });

    // Fire-and-forget: server-side LLM eval + Document Manager upload (NO DB write here),
    // placeholder swap, and analytics. Runs AFTER navigation so the customer proceeds
    // straight to image selection without waiting. Stash the returned draft PROMISE so the
    // coverage step can await this in-flight upload and persist the row once (with the
    // asrId) via save-concern-location.
    setConcernLocationDraftPromise(
      runConfirmUpload(capturedPin, {
        stockNumber,
        imagePipeline,
        spinStyle: spinStyleRef.current,
        captureImages: () => captureNow(capturedPin.id),
        updateUserSelections,
      })
    );

    // Advance to image selection right away — the background work above continues on its
    // own. (completeFormStep ignores dataSubset.title for the LOCATIONS route; the real
    // location lands via updateUserSelections when the detection returns.)
    handleNextRoute({ title: Location.Other });
  }, [handleNextRoute, updateUserSelections, pin, stockNumber, confirming, imagePipeline, captureNow]);

  // Camera → translate: with origin 0,0 a layer point p renders at T + z·p, so
  // 'center' keeps the pane midpoint fixed (T = (1−z)·pane/2) and 'point' pins the
  // marked spot to its target (T = t − z·p). Clamped so the scaled layer always
  // covers the pane (no background gaps), even as the slider re-zooms mid-step.
  let camX = ((1 - zoom) * paneSize.w) / 2;
  let camY = ((1 - zoom) * paneSize.h) / 2;
  if (camera.mode === 'point') {
    camX = Math.min(0, Math.max(-(zoom - 1) * paneSize.w, camera.t.x - zoom * camera.p.x));
    camY = Math.min(0, Math.max(-(zoom - 1) * paneSize.h, camera.t.y - zoom * camera.p.y));
  }

  // Slider floor tracks the same step-aware clamp applyZoom enforces (render reads the
  // step state; the handlers read stepRef — both agree).
  const sliderMin = step === 'confirm' ? CONFIRM_ZOOM_MIN : ZOOM_MIN;

  // ── Spinner: step 1 spin + tap → step 2 locked view + confirm card ────────
  return (
    <div className={styles['page-wrapper']}>
      {/* Cursors: open hand (grab) in the mark step — drag rotates; closed hand while
          actively dragging (inline, set by the pointer handlers); pointer hand in the
          confirm step, where a click places the dot and rotation is locked. */}
      <div
        ref={containerRef}
        className={`${styles.spinner} ${step === 'confirm' ? styles['step-confirm'] : ''} spinner-size-parent damage-picker-mode`}
      >
        {/* Zoom layer: one translate3d + scale wraps the spinner (single compositor-
            animated property = smooth camera). The pin overlay renders INSIDE it, so the
            dot shares the exact transform as the car pixels — locked in place during the
            move rather than chasing it a frame behind. The pin itself counter-scales with
            a matching transition so it keeps a constant visual size. Drag still reaches
            the spinner underneath in the mark step (rotate while zoomed; no pan mode). */}
        <div
          data-zoom-layer
          className={styles['zoom-layer']}
          style={{
            transform: `translate3d(${camX}px, ${camY}px, 0) scale(${zoom})`,
            transition: zoomAnimated ? `transform ${CAMERA_MS}ms ${CAMERA_EASE}` : undefined,
          }}
        >
          {/* Hold the car invisible until it's loaded + settled, so the MFE's swoop-in plays
              out behind the loading overlay. opacity (not display) keeps it laid out so the
              frames render and the canvas stays measurable while hidden. */}
          <div
            className={styles['spinner-reveal']}
            style={{ opacity: revealed ? 1 : 0 }}
          >
            <SpinnerMfe stockNumber={stockNumber} onFailed={handleMfeFailed} />
          </div>
          {canvasRect && (
            <div
              className={styles['pin-overlay']}
              style={{ left: canvasRect.left, top: canvasRect.top, width: canvasRect.width, height: canvasRect.height }}
            >
              {pin && (
                <div
                  className={styles.pin}
                  style={{
                    left: `${pin.canvasFraction.x * 100}%`,
                    top: `${pin.canvasFraction.y * 100}%`,
                    transform: `translate(-50%, -50%) scale(${1 / zoom})`,
                    transition: zoomAnimated ? `transform ${CAMERA_MS}ms ${CAMERA_EASE}` : undefined,
                  }}
                >
                  {/* Sonar rings pause during the camera move — their pulsing reads as
                      motion and makes the (stationary) dot look like it's drifting. */}
                  {!zoomAnimated && (
                    <>
                      <span className={styles['pin-sonar']} />
                      <span className={styles['pin-sonar2']} />
                    </>
                  )}
                  <span className={styles['pin-dot']} />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Loading overlay: covers the pane with the house spinner until the car is loaded +
            settled, then fades out. Blocks taps while up so nothing can be marked early. */}
        <div className={`${styles['loading-overlay']} ${revealed ? styles['loading-overlay-hidden'] : ''}`}>
          <LoadingSpinner />
        </div>

        {/* Step 1 heading overlaid on the top of the image (pointer-events:none so taps
            pass through to the spinner to mark/rotate). The confirm step's copy lives on
            the bottom card instead. */}
        {step === 'mark' && (
          <div className={styles['page-header']}>
            <h2>Show us where the issue is located</h2>
            <p>Spin the vehicle. Tap the one spot that matches where this issue actually is.</p>
          </div>
        )}

        {/* Bottom overlay: zoom slider always available; the confirm card pops up under it
            on the confirm step. data-zoom-ui keeps taps/drags here from marking or rotating
            (the container itself is pointer-events:none, so the transparent area still
            passes through to the spinner). */}
        <div className={styles['bottom-overlay']} data-zoom-ui>
          {/* translateY carries the slider's vertical position (hovering above the card in
              the confirm step, floating in the mark step) so its moves ride the compositor
              with the card's — flipping at the click means they travel + land together.
              Hidden (faded out, no pointer events) until the spinner's car canvas is
              actually rendered — canvasRect is the same "vehicle visible" gate the pin
              overlay uses — so it never floats over the empty loading pane. */}
          <div
            className={`${styles['zoom-bar']} ${!canvasRect ? styles['zoom-bar-hidden'] : ''}`}
            style={{
              transform: `translate(-50%, ${-(step === 'confirm' && cardHeight
                ? cardHeight + SLIDER_GAP_ABOVE_CARD_PX
                : SLIDER_FLOAT_PX + sliderInset)}px)`,
            }}
          >
            <input
              type="range"
              className={styles['zoom-slider']}
              style={{ ['--zoom-pct']: `${((zoom - sliderMin) / (ZOOM_MAX - sliderMin)) * 100}%` } as React.CSSProperties}
              min={sliderMin}
              max={ZOOM_MAX}
              step={0.1}
              value={zoom}
              onChange={(e) => applyZoom(Number(e.target.value))}
              aria-label="Zoom vehicle"
            />
          </div>

          {/* The card stays mounted while exiting: it translates down below the pane edge
              (compositor-only), then unmounts offscreen. */}
          {(step === 'confirm' || cardExiting) && (
            <div
              className={`${styles['confirm-card']} ${step !== 'confirm' ? styles['confirm-card-exiting'] : ''}`}
              ref={confirmCardRef}
              style={cardInset ? { paddingBottom: `calc(var(--spacing-lg) + ${cardInset}px)` } : undefined}
            >
              <h3>Confirm the exact spot</h3>
              <p>Tap a new spot to move the pin. The more accurate, the better.</p>
              <button type="button" className={styles['adjust-link']} onClick={adjustLocation}>
                Adjust location
              </button>
              {/* The LLM detection + image upload + clickstream all fire in the background
                  after Confirm, so the customer goes straight to image selection. */}
              <Button disabled={!hasPin || confirming} onClick={confirm}>
                Confirm location
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SpinnerLocation;
